import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Crown, 
  CheckCircle, 
  X, 
  Lock,
  ChartLineUp,
  TrendUp,
  Target,
  SpinnerGap,
  Star,
  Lightning
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { toast } from 'sonner';

export default function Pricing() {
  const { user, checkAuth } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [upgrading, setUpgrading] = useState(null);

  // Check for canceled checkout
  useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      toast.error(t('checkoutCanceled'));
    }
  }, [searchParams]);

  const handleUpgrade = async (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.plan === planId) {
      toast.info(`${t('alreadyPlan')} ${planId.toUpperCase()}!`);
      return;
    }

    // Can't downgrade through checkout
    const planOrder = { 'free': 0, 'pro': 1, 'vip': 2 };
    if (planOrder[planId] <= planOrder[user.plan]) {
      toast.info(t('downgradeContact'));
      return;
    }

    setUpgrading(planId);
    try {
      // Create Stripe checkout session
      const response = await api.post('/stripe/create-checkout-session', {
        plan: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(t('checkoutFailed'));
      setUpgrading(null);
    }
  };

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '€0',
      subtitle: t('startWithBasics'),
      icon: Lock,
      iconColor: 'text-white/50',
      cardBg: 'bg-white/5',
      borderColor: 'border-white/10',
      features: [
        { text: t('oneSlip3Days'), available: true },
        { text: t('basicAnalysis'), available: true },
        { text: t('limitedAccess'), available: true },
        { text: t('unlimitedSlips'), available: false },
        { text: t('advancedInsights'), available: false },
        { text: t('valueBets'), available: false }
      ],
      cta: user?.plan === 'free' ? t('currentPlan') : t('freePlan'),
      disabled: true,
      ctaStyle: 'bg-white/10 text-white/50 border border-white/20'
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '€9.99',
      priceDetail: t('perMonth'),
      subtitle: t('bestValueBettors'),
      icon: Star,
      iconColor: 'text-secondary',
      cardBg: 'bg-secondary/5',
      borderColor: 'border-secondary/50',
      badge: t('bestValue'),
      badgeColor: 'bg-secondary',
      features: [
        { text: t('fiveSlipsDay'), available: true, highlight: true },
        { text: t('fullAnalysis'), available: true },
        { text: t('confidenceScore'), available: true },
        { text: t('noAdsRestrictions'), available: true },
        { text: t('basicAdvancedInsights'), available: true },
        { text: t('priorityPicks'), available: false }
      ],
      cta: user?.plan === 'pro' ? t('currentPlan') : user?.plan === 'vip' ? t('lowerPlan') : t('upgradeToPro'),
      disabled: user?.plan === 'pro' || user?.plan === 'vip',
      ctaStyle: user?.plan === 'pro' 
        ? 'bg-white/10 text-white/50 border border-white/20'
        : 'bg-secondary text-background hover:opacity-90 shadow-lg'
    },
    {
      id: 'vip',
      name: 'VIP',
      price: '€19.99',
      priceDetail: t('perMonth'),
      subtitle: t('ultimateBetting'),
      icon: Crown,
      iconColor: 'text-primary',
      cardBg: 'bg-primary/5',
      borderColor: 'border-primary',
      badge: t('recommended'),
      badgeColor: 'bg-primary',
      recommended: true,
      features: [
        { text: t('unlimitedSlipsCaps'), available: true, highlight: true },
        { text: t('mostAdvancedAI'), available: true, highlight: true },
        { text: t('bestPicks'), available: true, highlight: true },
        { text: t('allFeaturesUnlocked'), available: true },
        { text: t('prioritySupport'), available: true },
        { text: t('earlyAccess'), available: true }
      ],
      cta: user?.plan === 'vip' ? t('currentPlan') : t('upgradeToVip'),
      disabled: user?.plan === 'vip',
      ctaStyle: user?.plan === 'vip'
        ? 'bg-white/10 text-white/50 border border-white/20'
        : 'bg-primary text-background hover:opacity-90 shadow-lg'
    }
  ];

  return (
    <div className="min-h-screen bg-background grain-overlay">
      {/* Header */}
      <nav className="border-b border-white/5 backdrop-blur-md bg-background/80">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <button 
            onClick={() => navigate('/dashboard')}
            className="text-white/70 hover:text-white transition-colors"
          >
            ← {t('backToDashboard')}
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-heading font-black text-white mb-4">
            {t('choosePlan')}
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            {t('unlockAIBetting')}
          </p>
        </div>

        {/* Plans Grid - 3 columns */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isCurrentPlan = user?.plan === plan.id;
            
            return (
              <div
                key={plan.id}
                className={`relative glass-surface rounded-sm p-6 transition-all duration-200 border-2 ${
                  plan.recommended 
                    ? `${plan.borderColor} transform md:scale-105 md:-my-4`
                    : `${plan.borderColor}`
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.badge && (
                  <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 ${plan.badgeColor} text-background font-bold text-xs uppercase rounded-full`}>
                    {plan.badge}
                  </div>
                )}

                <div className="text-center mb-6 pt-2">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${plan.cardBg} border ${plan.borderColor} mb-4`}>
                    <Icon weight="fill" className={`${plan.iconColor} text-2xl`} />
                  </div>
                  <h3 className="text-2xl font-heading font-black text-white mb-1">{plan.name}</h3>
                  <p className="text-white/50 text-sm mb-4">{plan.subtitle}</p>
                  <div className="mb-1">
                    <span className={`text-4xl font-mono font-black ${plan.id === 'vip' ? 'text-primary' : plan.id === 'pro' ? 'text-secondary' : 'text-white/50'}`}>
                      {plan.price}
                    </span>
                    {plan.priceDetail && (
                      <span className="text-white/50 text-base ml-1">{plan.priceDetail}</span>
                    )}
                  </div>
                  {plan.priceDetail && (
                    <div className="text-white/40 text-xs">Faturado mensalmente</div>
                  )}
                </div>

                <div className="space-y-2.5 mb-6">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      {feature.available ? (
                        <CheckCircle 
                          weight="fill" 
                          className={`${feature.highlight ? (plan.id === 'vip' ? 'text-primary' : 'text-secondary') : 'text-white/50'} text-lg mt-0.5 flex-shrink-0`}
                        />
                      ) : (
                        <X weight="bold" className="text-white/20 text-lg mt-0.5 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${feature.available ? 'text-white/80' : 'text-white/30 line-through'} ${feature.highlight ? 'font-semibold' : ''}`}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={plan.disabled || upgrading === plan.id}
                  className={`w-full px-6 py-3 rounded-full font-bold text-sm transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${plan.ctaStyle}`}
                  data-testid={`upgrade-${plan.id}-button`}
                >
                  {upgrading === plan.id ? (
                    <div className="flex items-center justify-center gap-2">
                      <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
                      A processar...
                    </div>
                  ) : (
                    plan.cta
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Current Subscription Status */}
        {user?.plan !== 'free' && (
          <div className={`glass-surface rounded-sm p-6 border max-w-md mx-auto mb-16 ${
            user?.plan === 'vip' ? 'border-primary/30' : 'border-secondary/30'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              {user?.plan === 'vip' ? (
                <Crown weight="fill" className="text-primary text-2xl" />
              ) : (
                <Star weight="fill" className="text-secondary text-2xl" />
              )}
              <h3 className="text-lg font-heading font-bold text-white">Estado da Subscrição</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/60">Plano:</span>
                <span className={`font-bold ${user?.plan === 'vip' ? 'text-primary' : 'text-secondary'}`}>
                  {user?.plan?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Estado:</span>
                <span className="text-risk-low font-bold">
                  {user?.subscription_status === 'active' ? 'Ativo' : user?.subscription_status || 'Ativo'}
                </span>
              </div>
              {user?.renewal_date && (
                <div className="flex justify-between">
                  <span className="text-white/60">Renovação:</span>
                  <span className="text-white">{new Date(user.renewal_date).toLocaleDateString('pt-PT')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Feature Comparison Table */}
        <div className="glass-surface rounded-sm p-8 border border-white/10 mb-16 overflow-x-auto">
          <h3 className="text-2xl font-heading font-bold text-white mb-6 text-center">Comparação de Funcionalidades</h3>
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 text-white/60 font-normal">Funcionalidade</th>
                <th className="text-center py-3 text-white/60 font-normal">Free</th>
                <th className="text-center py-3 text-secondary font-bold">Pro</th>
                <th className="text-center py-3 text-primary font-bold">VIP</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              <tr className="border-b border-white/5">
                <td className="py-3 text-white/80">SharpSlips</td>
                <td className="text-center text-white/50">1 / 3 dias</td>
                <td className="text-center text-secondary">5 / dia</td>
                <td className="text-center text-primary font-bold">Ilimitados</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 text-white/80">Análise de Jogos</td>
                <td className="text-center text-white/50">Básica</td>
                <td className="text-center text-white">Completa</td>
                <td className="text-center text-primary font-bold">Avançada IA</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 text-white/80">Confidence Score</td>
                <td className="text-center text-white/30">—</td>
                <td className="text-center text-secondary">✓</td>
                <td className="text-center text-primary">✓</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 text-white/80">Insights Avançados</td>
                <td className="text-center text-white/30">—</td>
                <td className="text-center text-white/50">Limitados</td>
                <td className="text-center text-primary">✓ Completos</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 text-white/80">Picks Prioritários</td>
                <td className="text-center text-white/30">—</td>
                <td className="text-center text-white/30">—</td>
                <td className="text-center text-primary">✓</td>
              </tr>
              <tr>
                <td className="py-3 text-white/80">Suporte Prioritário</td>
                <td className="text-center text-white/30">—</td>
                <td className="text-center text-white/30">—</td>
                <td className="text-center text-primary">✓</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ChartLineUp weight="duotone" className="text-primary text-2xl" />
            </div>
            <h4 className="font-heading font-bold text-white mb-2">Análise com IA</h4>
            <p className="text-white/60 text-sm">Algoritmos avançados analisam milhares de dados</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendUp weight="duotone" className="text-secondary text-2xl" />
            </div>
            <h4 className="font-heading font-bold text-white mb-2">Atualizações em Tempo Real</h4>
            <p className="text-white/60 text-sm">Análise instantânea quando jogos e odds mudam</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-risk-low/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target weight="duotone" className="text-risk-low text-2xl" />
            </div>
            <h4 className="font-heading font-bold text-white mb-2">Recomendações Inteligentes</h4>
            <p className="text-white/60 text-sm">IA deteta value bets e estratégias ótimas</p>
          </div>
        </div>
      </div>
    </div>
  );
}
